'use client';

import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { useCallback } from 'react';

/**
 * Inline call-to-action card the chat renders when the planner agent appends
 * a `{"type": "report", "report": {...}}` JSON block to its assistant text.
 *
 * Clicking the card routes to `/dani/{conversationId}/{reportId}`, which is
 * the same surface used by the report email links and the History →
 * Reports list.
 */
export function ReportLink({
    reportId,
    title,
    conversationId,
}: {
    reportId: number;
    title?: string;
    conversationId?: string;
}) {
    const router = useRouter();
    const params = useParams<{ conversationId?: string }>();
    const activeConversationId = conversationId ?? params?.conversationId ?? null;
    const reportHref = activeConversationId
        ? `/dani/${activeConversationId}/${reportId}`
        : undefined;

    const handleOpen = useCallback(() => {
        if (!reportHref) return;
        router.push(reportHref);
    }, [reportHref, router]);

    const isDisabled = !activeConversationId;
    const displayTitle = title?.trim() || 'Report';

    return (
        <div className="my-2 w-full">
            <div
                className={[
                    'group flex w-full items-center gap-3 rounded-xl border border-default-200/60 bg-content1 px-4 py-3 text-left shadow-sm transition-colors hover:border-mfour-green/60 hover:bg-content2/40',
                    isDisabled ? 'cursor-not-allowed opacity-60' : '',
                ].join(' ')}
            >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-mfour-green/10 text-mfour-green">
                    <Icon icon="lucide:file-text" width={18} height={18} />
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-tiny font-medium uppercase tracking-wide text-default-400">
                        Report ready
                    </span>
                    <span className="truncate text-small font-semibold text-foreground">
                        {displayTitle}
                    </span>
                </div>
                <div className="ml-auto shrink-0">
                    <Button
                        size="sm"
                        radius="full"
                        as={Link}
                        href={reportHref ?? '#'}
                        className="bg-mfour-green font-medium text-white"
                        endContent={<Icon icon="lucide:arrow-right" width={14} height={14} />}
                        onPress={handleOpen}
                        isDisabled={isDisabled}
                    >
                        Open
                    </Button>
                </div>
            </div>
        </div>
    );
}
